import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthService } from '@auth0/auth0-angular';

import { User, SsoConfig } from '../../+client-admin/users/user.model'
import { Observable, shareReplay, firstValueFrom, catchError, of, tap } from 'rxjs';
import { AuthInitializerService } from '../auth/auth-initializer.service';
import { SelfHostedAuthService } from '../auth/self-hosted-auth.service';

@Injectable({
  providedIn: 'root'
})
export class CurrentUserService {

  user$: Observable<User>;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private authInitializer: AuthInitializerService,
    private selfHostedAuth: SelfHostedAuthService
  ) {
  }

  async getUser(token?:string): Promise<User> {
    try {
      // Wait for auth token to be available before making authenticated API request
      await this.authInitializer.waitForAuth();

      // Only create a new request if we don't have a cached one
      // This prevents excessive API calls
      if (!this.user$) {
        // Create a properly cached request
        this.user$ = this.http.get<User>(`/api/user`).pipe(
          tap(user => this.persistSsoConfig(user)),
          // Use shareReplay to cache the result for all subscribers
          shareReplay(1),
          catchError(error => {
            console.error('Error fetching user:', error);
            // Clear cache so next attempt tries again
            this.user$ = null;
            // Retry once after a short delay if there's an error
            return new Promise(resolve => {
              setTimeout(() => {
                // Don't cache the retry if it's just a one-off promise
                resolve(firstValueFrom(this.http.get<User>(`/api/user`).pipe(
                  catchError(retryError => {
                    console.error('Retry error fetching user:', retryError);
                    return of(null as unknown as User);
                  })
                )));
              }, 1000);
            });
          })
        );
        
        // Reset the cache after 5 minutes to ensure fresh data occasionally
        setTimeout(() => {
          this.user$ = null;
        }, 300000); // 5 minutes
      } else {
        // console.log('Using cached user data');
      }
      
      // Use firstValueFrom instead of deprecated toPromise
      const user = await firstValueFrom(this.user$);
      await this.ensureTokenTenantMatch(user);
      return user;
    } catch (error) {
      if (error.message === 'REDIRECT_IN_PROGRESS') {
        // Auth redirect is happening - return a promise that never resolves
        // The page will redirect to Auth0, so hanging here is fine
        return new Promise(() => {});
      }
      console.error('Unexpected error in getUser:', error);
      return null;
    }
  }

  invalidateUserCache() {
    this.user$ = null;
  }

  /**
   * The API resolves the effective tenant per-request (UserMiddleware matches
   * the role to the request host, falling back to DB currentRole), but every
   * GraphQL call is scoped by the access token's x-hasura-current-client-id
   * claim, which is only minted at interactive login. Refresh-token exchanges
   * do NOT re-run the enrich action, so after an instance switch — or when
   * landing on app.* where the origin doesn't encode the tenant — the token
   * can silently point at a different tenant than the rest of the page,
   * rendering the nav for tenant A while every Hasura-backed body shows
   * tenant B. Detect the split and re-mint the session token for the
   * resolved tenant: a fresh session call for self-hosted (sovereign)
   * sessions, or one silent re-authorize for Auth0 (which re-runs the
   * enrich action). Guarded to a single attempt per session to avoid
   * redirect loops.
   */
  private async ensureTokenTenantMatch(user: User): Promise<void> {
    try {
      const resolvedClientId = (user as any)?.currentRole?.client?.id ?? (user as any)?.currentRole?.client_id;
      if (!user || !resolvedClientId) {
        return;
      }

      // Compare against the token the GraphQL layer actually sends
      // (bearer-token-link -> authInitializer.getToken()), which prefers a
      // self-hosted JWT over the Auth0 SDK token.
      let claimClientId: string | null = null;
      let bearerToken: string | null = null;
      try {
        bearerToken = await this.authInitializer.getToken();
        const payload = bearerToken ? JSON.parse(atob(bearerToken.split('.')[1])) : null;
        claimClientId = payload?.['https://hasura.io/jwt/claims']?.['x-hasura-current-client-id'] ?? null;
      } catch (e) {
        return;
      }

      if (!claimClientId || claimClientId === resolvedClientId) {
        sessionStorage.removeItem('s5_tenant_claim_retry');
        return;
      }

      if (sessionStorage.getItem('s5_tenant_claim_retry') === resolvedClientId) {
        console.warn(
          `CurrentUserService: token client claim ${claimClientId} still mismatches ` +
          `resolved tenant ${resolvedClientId} after re-auth; not retrying again.`
        );
        return;
      }
      sessionStorage.setItem('s5_tenant_claim_retry', resolvedClientId);

      // Self-hosted (sovereign) sessions: the SlapFive JWT is minted by the
      // api-server session endpoint and scoped to the request host's tenant.
      // Drop the stale token and re-mint via the shared-domain session —
      // no Auth0 round-trip involved.
      if (this.selfHostedAuth.hasSession() && bearerToken === this.selfHostedAuth.getToken()) {
        console.warn(
          `CurrentUserService: self-hosted token tenant ${claimClientId} != resolved tenant ` +
          `${resolvedClientId}; re-minting session token.`
        );
        this.selfHostedAuth.clear();
        const freshToken = await this.selfHostedAuth.restoreSession();
        if (freshToken) {
          this.authInitializer.setToken(freshToken);
          this.invalidateUserCache();
        }
        return;
      }


      console.warn(
        `CurrentUserService: token tenant ${claimClientId} != resolved tenant ${resolvedClientId}; ` +
        `re-authorizing silently to re-mint claims.`
      );
      this.authInitializer.setRedirectInProgress(true);
      const subdomain = (user as any)?.currentRole?.client?.subdomain || window.location.hostname.split('.')[0];
      const authorizationParams: any = {
        redirect_uri: `${window.location.origin}/auth/callback`,
        prompt: 'none'
      };
      if (subdomain) {
        authorizationParams.target_subdomain = subdomain;
      }
      this.authService.loginWithRedirect({
        appState: {
          target: window.location.pathname + window.location.search,
          originalHost: window.location.host,
          forceFreshToken: true
        },
        authorizationParams
      }).subscribe();
      // Navigation is in progress; never resolve so callers don't render
      // a page scoped to the stale tenant in the meantime.
      return new Promise(() => {});
    } catch (e) {
      console.warn('CurrentUserService: tenant/claim match check failed:', e);
    }
  }

  private static readonly SSO_CONFIG_KEY = 's5_sso_config';

  private persistSsoConfig(user: User): void {
    if (user?.ssoConfig) {
      try {
        localStorage.setItem(CurrentUserService.SSO_CONFIG_KEY, JSON.stringify(user.ssoConfig));
      } catch (e) {
        console.warn('CurrentUserService: failed to persist SSO config', e);
      }
    }
  }

  getSsoConfig(): SsoConfig | null {
    try {
      const raw = localStorage.getItem(CurrentUserService.SSO_CONFIG_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  async findUserByEmail(email: string) {
    if (!email) {
      return null;
    }
    return this.http.get(`/api/user/${email.toLowerCase()}`);
  }

  async getPermissions() {
    const user: User = await this.getUser();
    if (!user) {
      console.warn('getPermissions: user is null, returning default permissions');
      return {
        'canEditBoardTemplate': false
      };
    }
    return {
      'canEditBoardTemplate': user.superadmin || user.currentRole.permission_admin_app
    }
  }

  inviteNewUser(user: any) {
    return this.http
      .post('/api/email/invite-user', user);
  }

  createNewUser(user: any) {
    return this.http
      .post('/api/user', user);
  }
}
